import React, { useState } from "react";
import { LogOut, Crown, BookOpen } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { PUBLIC_CONTACT_EMAIL } from "../../../config/publicContact";
import type { Subscription } from "../../../hooks/useSubscription";
import DeleteAccountDialog from "./DeleteAccountDialog";
import LegalLinks from "../../misc/LegalLinks";

interface AccountSettingsProps {
  isPro: boolean;
  onUpgradeClick: () => void;
  subscription?: Subscription | null;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({
  isPro,
  onUpgradeClick,
  subscription = null,
}) => {
  const { user, signOut, isLoading } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  if (!user) return null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-warm-text-primary mb-1">
          Account
        </h3>
        <p className="text-sm text-warm-text-secondary">
          Your profile, legal pages, sign-out, and account deletion.
        </p>
      </div>

      {/* Profile card */}
      <div className="p-4 rounded-lg border border-warm-border bg-warm-bg-elevated">
        <div className="flex items-center gap-4">
          <img
            src={user.avatar}
            alt={user.name}
            className="w-12 h-12 rounded-full bg-warm-border flex-shrink-0"
          />
          <div className="min-w-0">
            <div className="font-medium text-warm-text-primary truncate">
              {user.name}
            </div>
            <div className="text-xs text-warm-text-tertiary truncate">
              {user.email}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {!isPro && (
          <button
            onClick={onUpgradeClick}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-warm-blue hover:bg-warm-blue-dark rounded-lg transition-colors"
          >
            <Crown size={16} />
            Subscribe
          </button>
        )}
        <a
          href="/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-warm-text-secondary bg-warm-bg-elevated hover:bg-warm-border-subtle rounded-lg transition-colors"
        >
          <BookOpen size={16} />
          Help & Guide
        </a>
        <button
          onClick={signOut}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-warm-text-secondary bg-warm-bg-elevated hover:bg-warm-border-subtle rounded-lg transition-colors disabled:opacity-50"
        >
          <LogOut size={16} />
          {isLoading ? "Signing out…" : "Sign Out"}
        </button>
      </div>

      <div>
        <h4 className="text-sm font-medium text-warm-text-primary mb-2">
          Legal
        </h4>
        <p className="text-sm text-warm-text-secondary mb-2">
          For legal or privacy questions, email{" "}
          <a
            href={`mailto:${PUBLIC_CONTACT_EMAIL}`}
            className="text-warm-blue hover:underline"
          >
            {PUBLIC_CONTACT_EMAIL}
          </a>
          .
        </p>
        <LegalLinks newTab />
      </div>

      <div className="pt-4 border-t border-warm-border space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-warm-text-primary mb-1">
            Delete account
          </h4>
          <p className="text-sm text-warm-text-secondary">
            Permanently remove your account and all of your JuDI data. This
            cannot be undone.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowDeleteDialog(true)}
          className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
          style={{ backgroundColor: "var(--color-recording)" }}
        >
          Delete account
        </button>
      </div>

      {showDeleteDialog ? (
        <DeleteAccountDialog
          onCancel={() => setShowDeleteDialog(false)}
          storeWarning={
            subscription?.provider === "apple"
              ? "apple"
              : subscription?.provider === "google"
                ? "play"
                : null
          }
        />
      ) : null}
    </div>
  );
};

export default AccountSettings;
